import {deleteUser, getUserById, updateUserAsync} from '@/api'
import {useAuthenticationStore} from '@/stores/useAuthenticationStore'
import type {
    ItemReference,
    TileConfiguration,
    TileSectionConfiguration,
    User
} from '@/types'
import {reactiveComputed, useAsyncState} from '@vueuse/core'
import {acceptHMRUpdate, defineStore, storeToRefs} from 'pinia'
import {ref, type Ref} from 'vue'

export interface UserStore {
    userId: Ref<string | null>
    user: User
    tileSectionConfigurations: TileSectionConfiguration[]

    loadUser(
        force?: boolean
    ): Promise<{ user: User; tileSectionConfigurations: TileSectionConfiguration[] }>

    updateUser(): Promise<void>
    deleteUserProfile(): Promise<void>
}

export const useUserStore = defineStore('userStore', function (): UserStore {
    const authStore = useAuthenticationStore()
    const {userId, userEmail, userFirstName, userLastname} = storeToRefs(authStore)
    const {state: _userState, execute: executeLoadUser} = useAsyncState<{ user: User,tileSectionConfigurations: TileSectionConfiguration[] }, []>(
        async () => {
            await authStore.initialize()
            // await until(userId).toMatch((id) => id !== null && id !== '')


            if (!userId.value)
                return {
                    user: {organizerFor: [] as ItemReference[]},
                    tileSectionConfigurations: [] as TileSectionConfiguration[]
                } as { user: User; tileSectionConfigurations: TileSectionConfiguration[] }

            const userDto = await getUserById(userId.value, userEmail.value as string)

            const user = userDto.user as User
            user.name ??= `${userFirstName?.value} ${userLastname?.value}`
            return {
                user: user,
                tileSectionConfigurations: userDto.tileSectionConfigurations
            }
        },
        {
            user: {organizerFor: [] as ItemReference[]},
            tileSectionConfigurations: [] as TileSectionConfiguration[]
        } as { user: User; tileSectionConfigurations: TileSectionConfiguration[] },
        {immediate: false, resetOnExecute: false}
    )
    const user = reactiveComputed(() => _userState.value.user)
    const tileSectionConfigurations = reactiveComputed(
        () => _userState.value.tileSectionConfigurations
    )

    const lastLoadedUserKey = ref<string | null>(null)
    let loadUserPromise: Promise<{ user: User; tileSectionConfigurations: TileSectionConfiguration[] }> | null = null

    async function loadUser(force = false): Promise<{ user: User; tileSectionConfigurations: TileSectionConfiguration[] }> {
        await authStore.initialize()

        const currentUserId = userId.value
        const currentUserEmail = userEmail.value ?? ''
        const loadKey = currentUserId ? `${currentUserId}:${currentUserEmail}` : null

        if (!currentUserId) {
            lastLoadedUserKey.value = null
            return _userState.value
        }

        if (loadUserPromise) {
            return await loadUserPromise
        }

        if (!force && lastLoadedUserKey.value === loadKey) {
            return _userState.value
        }

        loadUserPromise = executeLoadUser()

        try {
            const result = await loadUserPromise
            lastLoadedUserKey.value = loadKey
            return result
        } finally {
            loadUserPromise = null
        }
    }

    async function updateUser(): Promise<void> {
        _userState.value.user = await updateUserAsync(user)
        lastLoadedUserKey.value = null
        await loadUser(true)
    }

    async function deleteUserProfile(): Promise<void> {
        await authStore.initialize()

        if (!userId.value) {
            throw new Error('Bruger-id mangler — profil kan ikke slettes.')
        }

        await deleteUser(userId.value)
        lastLoadedUserKey.value = null
    }

    return {
        userId,
        user,
        tileSectionConfigurations,
        loadUser,
        updateUser,
        deleteUserProfile
    }
})
// make sure to pass the right store definition, `useAuth` in this case.
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
